'use client';

import { c as _c } from "react/compiler-runtime";
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Fragment } from 'react';
import { Button } from '@src/components/Button/Button';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { useTopNav } from '@src/components/TopNavV4/TopNavContext';
import { configPublic } from '@src/config.public';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { useTrackSelectNavCta } from '@src/utils/analytics.util';
interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
}
const MenuItem: React.FC<MenuItemProps> = t0 => {
  const $ = _c(12);
  const {
    icon,
    label,
    href
  } = t0;
  const trackSelectNavCta = useTrackSelectNavCta();
  const t1 = href ?? "";
  let t2;
  if ($[0] !== href || $[1] !== trackSelectNavCta) {
    t2 = () => {
      trackSelectNavCta({
        "cta url": href
      });
    };
    $[0] = href;
    $[1] = trackSelectNavCta;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== icon) {
    t3 = <div className="mr-3 flex size-6 items-center justify-center">{icon}</div>;
    $[3] = icon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label) {
    t4 = <span className="text-body-sm">{label}</span>;
    $[5] = label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex items-center rounded-md"><Link href={t1} rel="noopener" target="_blank" className="flex items-center rounded-md px-4 py-1 hover:font-bold hover:underline" onClick={t2}>{t3}{t4}</Link></div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export const TopNavAccountPanelContent = () => {
  const $ = _c(25);
  const {
    setAccountExpanded,
    account
  } = useTopNav();
  const {
    user,
    getLoginHref
  } = useSession();
  const loginHref = getLoginHref();
  const trackSelectNavCta = useTrackSelectNavCta();
  const pathname = usePathname();
  const {
    t,
    locale
  } = useLocale("common");
  const logOutHref = `/logout?redirectUrl=${pathname}`;
  let t0;
  if ($[0] !== account?.welcomeCopy || $[1] !== user?.firstName || $[2] !== user?.lastName) {
    t0 = user?.firstName && user?.firstName?.toLowerCase() !== "guest" ? `${account?.welcomeCopy}, ${user?.firstName} ${user?.lastName}.` : `${account?.welcomeCopy}!`;
    $[0] = account?.welcomeCopy;
    $[1] = user?.firstName;
    $[2] = user?.lastName;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const welcomeText = t0;
  const isLoggedIn = Boolean(user?.email);
  let t1;
  if ($[4] !== account?.links || $[5] !== trackSelectNavCta) {
    t1 = () => <div className="flex flex-col">{account?.links?.map((link, i) => <LocalizedLink key={i} href={link?.href ?? ""} rel={link?.newTab ? "noopener noreferrer" : ""} target={link?.newTab ? "_blank" : "_self"} className="group my-1 flex items-center justify-between py-1 text-body-base hover:underline" onClick={() => trackSelectNavCta({
        "cta url": link?.href
      })}><div className="flex items-center font-bold">{link?.title}</div><ChevronRightIcon className="size-5 text-bodi-gray-medium group-hover:text-bodi-accent-bright-500" /></LocalizedLink>)}</div>;
    $[4] = account?.links;
    $[5] = trackSelectNavCta;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const renderLinks = t1;
  let t2;
  if ($[7] !== account?.loggedInLinks || $[8] !== account?.signOutCopy || $[9] !== account?.title || $[10] !== locale || $[11] !== logOutHref || $[12] !== renderLinks || $[13] !== t || $[14] !== trackSelectNavCta || $[15] !== welcomeText) {
    t2 = () => <div className="flex flex-col pt-9 lg:pt-5"><div className="text-heading-display-3 mb-1">{account?.title}</div><div className="mb-8 text-body-base text-bodi-gray-butters">{welcomeText}</div>{!configPublic.featureFlags.onesiteEnabled && <div className="mb-5 flex flex-col gap-1 overflow-hidden rounded-[30px] border border-bodi-gray-dark pb-2 transition-colors">{account?.loggedInLinks?.map((link_0, i_0) => {
          const createBODurl = path => `${configPublic.general.bodUrl}${path}?code=BODI_ACCOUNT_NAV&locale=${locale.code.replace("-", "_")}`;
          return <Fragment key={i_0}><LocalizedLink href={link_0?.href ?? ""} rel={link_0?.newTab ? "noopener noreferrer" : ""} target={link_0?.newTab ? "_blank" : "_self"} className="flex items-center justify-between gap-2 bg-bodi-white/5 p-4 hover:bg-bodi-white/10" onClick={() => trackSelectNavCta({
              "cta url": link_0?.href
            })}><div className="flex items-center gap-2"><Image className="shrink-0 rounded-xl bg-bodi-gray-dark object-cover" width={48} height={48} src={link_0?.imageUrl ?? ""} alt={link_0?.imageAlt ?? ""} /><div className="flex flex-col"><div className="text-heading-5">{link_0?.title}</div><div className="text-body-sm text-bodi-gray-butters">{link_0?.subtitle}</div></div></div><ChevronRightIcon className="size-6 shrink-0 stroke-[2px]" /></LocalizedLink><MenuItem icon={<Image src="/images/squares_four.svg" alt="squares_four" width={16} height={16} />} label={t("my-dashboard")} href={createBODurl("/dashboard")} /><MenuItem icon={<Image src="/images/play_circle.svg" alt="play_circle" width={16} height={16} />} label={t("digital-purchases")} href={createBODurl("/digital-purchases")} /><MenuItem icon={<Image src="/images/heart.svg" alt="heart" width={16} height={16} />} label={t("favorites")} href={createBODurl("/favorites")} /><MenuItem icon={<Image src="/images/trophy.svg" alt="trophy" width={16} height={16} />} label={t("badges")} href={createBODurl("/dashboard/badges")} /><MenuItem icon={<Image src="/images/note_pencil.svg" alt="note_pencil" width={16} height={16} />} label={t("tracking")} href={createBODurl("/tracking")} /><MenuItem icon={<Image src="/images/trend_up.svg" alt="trend_up" width={16} height={16} />} label={t("my-progress")} href={createBODurl("/tracking/my-progress")} /></Fragment>;
        })}</div>}{renderLinks()}<a href={logOutHref} className="mt-8 self-center font-medium text-bodi-accent-bright-500 underline">{account?.signOutCopy}</a></div>;
    $[7] = account?.loggedInLinks;
    $[8] = account?.signOutCopy;
    $[9] = account?.title;
    $[10] = locale;
    $[11] = logOutHref;
    $[12] = renderLinks;
    $[13] = t;
    $[14] = trackSelectNavCta;
    $[15] = welcomeText;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const renderLoggedInContent = t2;
  const renderLoggedOutContent = () => <div className="pt-9 lg:pt-4"><div className="mb-8"><div className="text-heading-display-3">{account?.loggedOutTitle}</div><div className="pt-1 text-body-base text-bodi-gray-butters">{account?.loggedOutSubtitle}</div></div><div className="flex flex-col gap-4"><a onClick={() => {
        trackSelectNavCta({
          "cta url": loginHref
        });
      }} data-testid="logInBtn" href={loginHref} className="flex cursor-pointer gap-2 hover:text-bodi-white"><Button size="medium" variant="black" className="w-full border border-solid border-bodi-gray-medium">{account?.logInCopy}</Button></a><LocalizedLink href="/offers" onClick={() => {
        trackSelectNavCta({
          "cta url": "/offers"
        });
      }} data-testid="offersLink" className="flex cursor-pointer gap-2 hover:text-bodi-white"><Button size="medium" variant="primary" className="w-full">{account?.bodiCta?.title}</Button></LocalizedLink></div></div>;
  const t3 = "min-h-screen bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 p-4 text-white lg:p-8";
  let t4;
  if ($[17] !== setAccountExpanded) {
    t4 = () => setAccountExpanded(false);
    $[17] = setAccountExpanded;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <XMarkIcon className="size-6 stroke-[2px]" />;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4) {
    t6 = <button className="absolute top-4 right-4 rounded-md p-1 hover:bg-bodi-gray-dark" onClick={t4}>{t5}</button>;
    $[20] = t4;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = isLoggedIn ? renderLoggedInContent() : renderLoggedOutContent();
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <div className={t3}>{t6}{t7}</div>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};